import { format as dateFnsFormat, formatDistanceToNow, Locale } from 'date-fns';
import { enUS, es, fr, de, pt, ptBR, it, zhCN, ja, ko, ar, ru, hi, tr, nl, pl, sv, da, nb, fi, uk, cs, ro, hu, el, th, vi, id as idLocale, ms, he } from 'date-fns/locale';
import type { toZonedTime as ToZonedTimeFn, fromZonedTime as FromZonedTimeFn, formatInTimeZone as FormatInTimeZoneFn } from 'date-fns-tz';

// Dynamic import for date-fns-tz to handle build timing issues
let toZonedTime: typeof ToZonedTimeFn | null = null;
let fromZonedTime: typeof FromZonedTimeFn | null = null;
let formatInTimeZone: typeof FormatInTimeZoneFn | null = null;

// Lazy load date-fns-tz functions
const loadTimezoneFunctions = async () => {
  try {
    const tzModule = await import('date-fns-tz');
    toZonedTime = tzModule.toZonedTime;
    fromZonedTime = tzModule.fromZonedTime;
    formatInTimeZone = tzModule.formatInTimeZone;
  } catch {
    // date-fns-tz not available, timezone functions will use fallbacks
  }
};

// Initialize on module load
loadTimezoneFunctions();

// locale-data: date-fns-mapping - Maps app locale codes to date-fns Locale objects
// This is a library constraint: date-fns requires importing specific locale modules.
// The mapping is static because date-fns locales are compile-time imports, not database-driven.
// We use base language locales where regional variants don't have dedicated date-fns support.
const localeMap: Record<string, Locale> = {
  'en-US': enUS,
  'en-GB': enUS,
  'en-CA': enUS,
  'en-AU': enUS,
  'es-ES': es,
  'es-MX': es,
  'es-AR': es,
  'es-CO': es,
  'es-CL': es,
  'pt-BR': ptBR,
  'pt-PT': pt,
  'fr-FR': fr,
  'fr-CA': fr,
  'fr-BE': fr,
  'de-DE': de,
  'de-AT': de,
  'de-CH': de,
  'it-IT': it,
  'zh-CN': zhCN,
  'zh-TW': zhCN,
  'zh-HK': zhCN,
  'ja-JP': ja,
  'ko-KR': ko,
  'ar-SA': ar,
  'ar-AE': ar,
  'ar-EG': ar,
  'ru-RU': ru,
  'hi-IN': hi,
  'tr-TR': tr,
  'nl-NL': nl,
  'nl-BE': nl,
  'pl-PL': pl,
  'sv-SE': sv,
  'da-DK': da,
  'nb-NO': nb,
  'fi-FI': fi,
  'uk-UA': uk,
  'cs-CZ': cs,
  'ro-RO': ro,
  'hu-HU': hu,
  'el-GR': el,
  'th-TH': th,
  'vi-VN': vi,
  'id-ID': idLocale,
  'ms-MY': ms,
  'he-IL': he,
};

// Base-language fallback: if exact locale not found, try matching by language prefix
function resolveLocale(locale: string): Locale {
  if (localeMap[locale]) return localeMap[locale];
  const lang = locale.split('-')[0];
  const baseMatch = Object.entries(localeMap).find(([key]) => key.startsWith(lang + '-'));
  return baseMatch ? baseMatch[1] : enUS;
}

export interface LocaleSettings {
  locale: string;
  language: string;
  country: string;
  timezone: string;
  currency: string;
  dateFormat: string;
  numberFormat: string;
  firstDayOfWeek: number;
  isRTL: boolean;
  displayName: string;
}

export class LocaleFormatter {
  constructor(private settings: LocaleSettings) {}

  private resolveDateStyle(dateFormat: string): 'short' | 'medium' | 'long' | 'full' {
    switch (dateFormat) {
      case 'short':
      case 'medium':
      case 'long':
      case 'full':
        return dateFormat;
      default:
        return 'medium';
    }
  }

  /**
   * Format a date according to user's locale and preferences
   */
  formatDate(
    date: Date | string | number | null | undefined,
    style?: 'short' | 'medium' | 'long' | 'full'
  ): string {
    if (date == null) return '';
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    if (Number.isNaN(dateObj.getTime())) return '';
    const locale = resolveLocale(this.settings.locale);
    const preferredStyle = style ?? this.resolveDateStyle(this.settings.dateFormat);

    const formatMap = {
      short: 'P',           // 04/29/2023
      medium: 'PP',         // Apr 29, 2023
      long: 'PPP',          // April 29th, 2023
      full: 'PPPP',         // Saturday, April 29th, 2023
    };

    return dateFnsFormat(dateObj, formatMap[preferredStyle], { locale });
  }

  /**
   * Format time according to user's locale
   */
  formatTime(date: Date | string | number | null | undefined): string {
    if (date == null) return '';
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    if (Number.isNaN(dateObj.getTime())) return '';
    const locale = resolveLocale(this.settings.locale);
    return dateFnsFormat(dateObj, 'p', { locale }); // 5:00 PM
  }

  /**
   * Format date and time together
   */
  formatDateTime(
    date: Date | string | number | null | undefined,
    style: 'short' | 'medium' | 'long' = 'medium',
  ): string {
    if (date == null) return '';
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    if (Number.isNaN(dateObj.getTime())) return '';
    const locale = resolveLocale(this.settings.locale);

    const formatMap = {
      short: 'Pp',       // 04/29/2023, 5:00 PM
      medium: 'PPp',     // Apr 29, 2023, 5:00 PM
      long: 'PPPp',      // April 29th, 2023 at 5:00 PM
    };

    return dateFnsFormat(dateObj, formatMap[style], { locale });
  }

  /**
   * Format relative time (e.g., "2 hours ago").
   * Returns '' for null / undefined / invalid Date input — date-fns'
   * formatDistanceToNow throws `RangeError: Invalid time value` for
   * those, which propagated up to the React error boundary and crashed
   * the whole app whenever a publication had a null timestamp. The
   * component-level `? :` guards in PR #230 stopped null reaching this
   * function from the editorial blocks, but defense in depth here means
   * the next forgotten guard elsewhere doesn't crash the app.
   */
  formatRelativeTime(date: Date | string | number | null | undefined): string {
    if (date == null) return '';
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    if (Number.isNaN(dateObj.getTime())) return '';
    const locale = resolveLocale(this.settings.locale);
    return formatDistanceToNow(dateObj, { addSuffix: true, locale });
  }

  /**
   * Format relative time in English (for admin areas).
   * Same null/invalid handling as formatRelativeTime.
   */
  formatRelativeTimeEnglish(date: Date | string | number | null | undefined): string {
    if (date == null) return '';
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    if (Number.isNaN(dateObj.getTime())) return '';
    return formatDistanceToNow(dateObj, { addSuffix: true, locale: enUS });
  }

  /**
   * Format a number according to user's locale
   */
  formatNumber(num: number, options?: Intl.NumberFormatOptions): string {
    return new Intl.NumberFormat(this.settings.locale, options).format(num);
  }

  /**
   * Format a number in compact notation (1.2K, 1.5M)
   */
  formatCompactNumber(num: number): string {
    return new Intl.NumberFormat(this.settings.locale, {
      notation: 'compact',
      compactDisplay: 'short',
    }).format(num);
  }

  /**
   * Format currency value
   */
  formatCurrency(amount: number, currency?: string): string {
    const currencyCode = currency || this.settings.currency;
    return new Intl.NumberFormat(this.settings.locale, {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  }

  /**
   * Format currency in compact notation
   */
  formatCompactCurrency(amount: number, currency?: string): string {
    const currencyCode = currency || this.settings.currency;
    return new Intl.NumberFormat(this.settings.locale, {
      style: 'currency',
      currency: currencyCode,
      notation: 'compact',
      compactDisplay: 'short',
    }).format(amount);
  }

  /**
   * Format percentage value
   */
  formatPercent(value: number, decimals: number = 0): string {
    return new Intl.NumberFormat(this.settings.locale, {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  }

  /**
   * Convert date to user's timezone
   */
  convertToUserTimezone(date: Date | string | number): Date {
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    
    // Fallback if date-fns-tz not loaded
    if (!toZonedTime) {
      return dateObj;
    }
    
    try {
      return toZonedTime(dateObj, this.settings.timezone);
    } catch {
      return dateObj;
    }
  }

  /**
   * Convert date from user's timezone to UTC
   */
  convertFromUserTimezone(date: Date | string | number): Date {
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    
    // Fallback if date-fns-tz not loaded
    if (!fromZonedTime) {
      return dateObj;
    }
    
    try {
      return fromZonedTime(dateObj, this.settings.timezone);
    } catch {
      return dateObj;
    }
  }

  /**
   * Format date in user's timezone
   */
  formatInUserTimezone(date: Date | string | number, style: 'short' | 'medium' | 'long' = 'medium'): string {
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    const locale = localeMap[this.settings.locale] || enUS;
    
    const formatMap = {
      short: 'P',
      medium: 'PP',
      long: 'PPP',
    };

    // Fallback to regular formatting if date-fns-tz not loaded
    if (!formatInTimeZone) {
      return dateFnsFormat(dateObj, formatMap[style], { locale });
    }

    try {
      return formatInTimeZone(dateObj, this.settings.timezone, formatMap[style], { locale });
    } catch {
      return dateFnsFormat(dateObj, formatMap[style], { locale });
    }
  }

  /**
   * Format date and time in user's timezone
   */
  formatDateTimeInUserTimezone(date: Date | string | number, style: 'short' | 'medium' | 'long' = 'medium'): string {
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    const locale = localeMap[this.settings.locale] || enUS;
    
    const formatMap = {
      short: 'Pp',
      medium: 'PPp',
      long: 'PPPp',
    };

    // Fallback to regular formatting if date-fns-tz not loaded
    if (!formatInTimeZone) {
      return dateFnsFormat(dateObj, formatMap[style], { locale });
    }

    try {
      return formatInTimeZone(dateObj, this.settings.timezone, formatMap[style], { locale });
    } catch {
      return dateFnsFormat(dateObj, formatMap[style], { locale });
    }
  }

  /**
   * Format a duration in milliseconds to a human-readable string
   * E.g., 1234ms -> "1.2s", 65000ms -> "1m 5s", 3661000ms -> "1h 1m"
   */
  formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  }

  /**
   * Format a duration in seconds to a human-readable string
   * E.g., 1.5 -> "1.5s", 65 -> "1m 5s"
   */
  formatDurationSeconds(seconds: number): string {
    if (seconds < 60) return `${this.formatNumber(seconds, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (mins < 60) return `${mins}m ${secs}s`;
    const hours = Math.floor(mins / 60);
    return `${hours}h ${mins % 60}m`;
  }

  /**
   * Format bytes to human-readable size with locale-aware decimal separator
   */
  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);
    return `${this.formatNumber(value, { maximumFractionDigits: 1 })} ${sizes[i]}`;
  }
}
